/* URBRAND Street Commerce 5.9.7 — cart drawer and global predictive search. */
(() => {
  'use strict';

  const cfg = window.URB || {};
  const strings = cfg.strings || {};
  const $ = (selector, root = document) => root?.querySelector(selector) || null;
  const $$ = (selector, root = document) => [...(root?.querySelectorAll(selector) || [])];
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const recentKey = 'urbrand:recent-searches:v2';
  const recentLegacyKey = 'urbrand:recent-searches:v1';
  const termsKey = 'urbrand:cart-terms:v1';
  const termsLegacyKey = 'urbrand:cart-terms';
  const bool = (value) => String(value) === 'true';
  const selectorEscape = (value) => window.CSS?.escape ? window.CSS.escape(String(value)) : String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');

  const text = (key, fallback = '', vars = {}) => {
    let value = strings[key] || fallback;
    Object.entries(vars).forEach(([name, replacement]) => {
      value = String(value).split(`__${name.toUpperCase()}__`).join(String(replacement));
    });
    return value;
  };

  const notify = (message) => {
    const toast = $('#Toast');
    if (!toast || !message) return;
    toast.textContent = message;
    toast.classList.add('show');
    window.clearTimeout(window.urbToast);
    window.urbToast = window.setTimeout(() => toast.classList.remove('show'), 2800);
  };

  const safeUrl = (value = '') => {
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
    } catch {
      return '#';
    }
  };

  const imageUrl = (value, width = 420) => {
    const source = typeof value === 'string' ? value : value?.url || value?.src || '';
    const safe = safeUrl(source);
    if (safe === '#') return '';
    const url = new URL(safe);
    url.searchParams.set('width', String(width));
    return url.href;
  };

  const toCents = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value !== 'string') {
      const numeric = Number(value || 0);
      return Number.isFinite(numeric) ? numeric : 0;
    }
    const cleaned = value.trim().replace(/[^0-9.,-]/g, '');
    if (!cleaned) return 0;
    if (/^-?\d+$/.test(cleaned)) return Number(cleaned);
    const dotCount = (cleaned.match(/\./g) || []).length;
    const commaCount = (cleaned.match(/,/g) || []).length;
    const onlySeparator = dotCount ? '.' : ',';
    let decimalSeparator = '';
    if (dotCount && commaCount) {
      decimalSeparator = cleaned.lastIndexOf('.') > cleaned.lastIndexOf(',') ? '.' : ',';
    } else {
      const groups = cleaned.replace('-', '').split(onlySeparator);
      const lastLength = groups.at(-1)?.length || 0;
      if (lastLength > 0 && lastLength <= 2) decimalSeparator = onlySeparator;
    }
    if (!decimalSeparator) {
      const integer = Number(cleaned.replace(/[.,]/g, ''));
      return Number.isFinite(integer) ? integer * 100 : 0;
    }
    const decimalIndex = cleaned.lastIndexOf(decimalSeparator);
    const integerPart = cleaned.slice(0, decimalIndex).replace(/[.,]/g, '') || '0';
    const decimalPart = cleaned.slice(decimalIndex + 1).replace(/[.,]/g, '').slice(0, 2).padEnd(2, '0');
    const numeric = Number(`${integerPart}.${decimalPart}`);
    return Number.isFinite(numeric) ? Math.round(numeric * 100) : 0;
  };

  const formatMoney = (value) => {
    const cents = toCents(value);
    try {
      return new Intl.NumberFormat(cfg.locale || document.documentElement.lang || 'pt-BR', {
        style: 'currency',
        currency: cfg.currency || 'BRL'
      }).format(cents / 100);
    } catch {
      return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
    }
  };

  const route = (name, fallback) => {
    const value = cfg.routes?.[name] || fallback;
    if (!value) return fallback;
    return value.endsWith('.js') || !fallback.endsWith('.js') ? value : `${value}.js`;
  };

  const syncBodyLock = () => {
    const modalOpen = Boolean(document.querySelector('dialog[open]'));
    const panelOpen = Boolean(document.querySelector('[data-mobile-menu][aria-hidden="false"], [data-filter-drawer].open'));
    document.body.classList.toggle('locked', modalOpen || panelOpen);
  };

  const closeOtherDialogs = (exception) => {
    $$('dialog[open]').forEach((dialog) => {
      if (dialog === exception) return;
      try { dialog.close(); } catch { dialog.removeAttribute('open'); }
    });
    syncBodyLock();
  };

  const focusable = (root) => $$(FOCUSABLE, root).filter((node) => !node.hidden && node.getAttribute('aria-hidden') !== 'true' && node.offsetParent !== null);

  const trapFocus = (event, root) => {
    if (event.key !== 'Tab') return;
    const nodes = focusable(root);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openDialog = (dialog, focusSelector) => {
    if (!dialog) return;
    closeOtherDialogs(dialog);
    dialog.urbPreviousFocus = document.activeElement;
    try {
      if (!dialog.open) dialog.showModal();
    } catch {
      dialog.setAttribute('open', '');
    }
    syncBodyLock();
    const target = focusSelector ? $(focusSelector, dialog) : $('[data-autofocus]', dialog) || focusable(dialog)[0];
    window.setTimeout(() => target?.focus({ preventScroll: true }), 40);
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    try {
      if (dialog.open) dialog.close();
    } catch {
      dialog.removeAttribute('open');
    }
    syncBodyLock();
    const previous = dialog.urbPreviousFocus;
    if (previous && document.contains(previous)) window.setTimeout(() => previous.focus({ preventScroll: true }), 0);
    dialog.urbPreviousFocus = null;
  };

  const setupDialog = (dialog) => {
    if (!dialog || dialog.dataset.urbDialogReady === 'true') return;
    dialog.dataset.urbDialogReady = 'true';
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog(dialog);
    });
    dialog.addEventListener('close', syncBodyLock);
    dialog.addEventListener('keydown', (event) => trapFocus(event, dialog));
  };

  // Shared by product, wishlist and retention dialogs to avoid competing scroll locks.
  window.URBDialog = Object.freeze({
    open: openDialog,
    close: closeDialog,
    setup: setupDialog,
    sync: syncBodyLock
  });

  /* ------------------------------------------------------------------ *
   * Cart drawer — server-rendered Liquid, updated with bundled sections.
   * ------------------------------------------------------------------ */
  class CartDrawerController {
    constructor() {
      this.quantityTimers = new Map();
      this.noteTimer = null;
      this.queue = Promise.resolve();
      this.boundClick = this.onClick.bind(this);
      this.boundChange = this.onChange.bind(this);
      this.boundInput = this.onInput.bind(this);
      this.boundSubmit = this.onSubmit.bind(this);
      document.addEventListener('click', this.boundClick);
      document.addEventListener('change', this.boundChange);
      document.addEventListener('input', this.boundInput);
      document.addEventListener('submit', this.boundSubmit);
      this.prepareCurrentDialog();
      this.syncCountFromMarkup();
    }

    get section() { return $('#shopify-section-cart-drawer') || $('.cart-drawer-section'); }
    get dialog() { return $('#CartDrawer'); }

    prepareCurrentDialog() {
      setupDialog(this.dialog);
      this.restoreTerms();
    }

    updateStatus(message) {
      const node = $('[data-cart-status]', this.dialog || document);
      if (node) node.textContent = message || '';
    }

    syncCountFromMarkup() {
      const count = Number(this.dialog?.dataset.cartCountValue || 0);
      $$('[data-cart-count]').forEach((node) => {
        node.textContent = String(count);
        node.hidden = count === 0;
      });
      const drawerCount = $('[data-cart-drawer-count]', this.dialog || document);
      if (drawerCount) drawerCount.textContent = `${count} ${count === 1 ? 'item' : 'itens'}`;
      return count;
    }

    restoreTerms() {
      const checkbox = $('[data-cart-terms]', this.dialog || document);
      const checkout = $('[data-cart-checkout]', this.dialog || document);
      if (!checkbox || !checkout) return;
      let accepted = false;
      try {
        let stored = sessionStorage.getItem(termsKey);
        if (stored === null) {
          stored = sessionStorage.getItem(termsLegacyKey);
          if (stored !== null) sessionStorage.setItem(termsKey, stored);
        }
        accepted = stored === '1';
      } catch { /* ignored */ }
      checkbox.checked = accepted;
      checkout.setAttribute('aria-disabled', String(!accepted));
    }

    setLoading(loading, lineKey = '') {
      this.dialog?.classList.toggle('is-loading', loading && !lineKey);
      const body = $('[data-cart-body]', this.dialog || document);
      if (body) body.setAttribute('aria-busy', String(loading));
      if (lineKey) {
        const line = $(`[data-cart-line="${selectorEscape(lineKey)}"]`, this.dialog || document);
        line?.classList.toggle('is-busy', loading);
        line?.querySelectorAll('button,input').forEach((node) => { node.disabled = loading; });
      }
    }

    sectionRequestUrl() {
      const url = new URL(cfg.routes?.cart || '/cart', window.location.origin);
      url.searchParams.set('section_id', 'cart-drawer');
      return url.href;
    }

    async fetchSection() {
      const response = await fetch(this.sectionRequestUrl(), {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(text('cartSyncError', 'Não foi possível sincronizar a sacola.'));
      return response.text();
    }

    extractSection(html) {
      if (!html) return null;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      return documentFragment.querySelector('#shopify-section-cart-drawer')
        || documentFragment.querySelector('.cart-drawer-section')
        || (() => {
          const wrapper = document.createElement('div');
          wrapper.id = 'shopify-section-cart-drawer';
          wrapper.className = 'shopify-section cart-drawer-section';
          wrapper.innerHTML = html;
          return wrapper;
        })();
    }

    replaceSection(html, { open = false, announce = '' } = {}) {
      const next = this.extractSection(html);
      const current = this.section;
      if (!next || !current) return false;
      const wasOpen = Boolean(this.dialog?.open);
      current.replaceWith(next);
      this.prepareCurrentDialog();
      const count = this.syncCountFromMarkup();
      if (open || wasOpen) openDialog(this.dialog, '[data-cart-close]');
      if (announce) this.updateStatus(announce);
      document.dispatchEvent(new CustomEvent('urb:cart:updated', { detail: { itemCount: count } }));
      document.dispatchEvent(new CustomEvent('urb:cart:rendered', { detail: { itemCount: count, drawer: this.dialog } }));
      return true;
    }

    async refresh({ open = false } = {}) {
      if (!this.section) {
        window.location.assign(cfg.routes?.cart || '/cart');
        return false;
      }
      this.setLoading(true);
      try {
        const html = await this.fetchSection();
        this.replaceSection(html, { open });
        return true;
      } catch (error) {
        if (open && this.dialog) openDialog(this.dialog, '[data-cart-close]');
        notify(error.message || text('cartSyncError', 'Não foi possível sincronizar a sacola.'));
        return false;
      } finally {
        this.setLoading(false);
      }
    }

    enqueue(task) {
      this.queue = this.queue.catch(() => {}).then(task);
      return this.queue;
    }

    async request(path, payload, { formData = false, lineKey = '', open = false, announce = '' } = {}) {
      return this.enqueue(async () => {
        this.setLoading(true, lineKey);
        try {
          let body;
          const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
          if (formData) {
            body = new FormData();
            for (const [key, value] of payload.entries()) body.append(key, value);
            body.set('sections', 'cart-drawer');
            body.set('sections_url', window.location.pathname);
          } else {
            body = JSON.stringify({
              ...payload,
              sections: ['cart-drawer'],
              sections_url: window.location.pathname
            });
            headers['Content-Type'] = 'application/json';
          }
          const response = await fetch(path, { method: 'POST', headers, body });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.description || data.message || text('cartUpdateError', 'Não foi possível atualizar a sacola.'));
          const html = data.sections?.['cart-drawer'];
          if (html) this.replaceSection(html, { open, announce });
          else await this.refresh({ open });
          return data;
        } finally {
          this.setLoading(false, lineKey);
        }
      });
    }

    async add(formData, button, options = {}) {
      if (!(formData instanceof FormData)) return false;
      if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
      }
      const shouldOpen = options.open ?? bool(this.dialog?.dataset.openAfterAdd ?? cfg.cartDrawer);
      try {
        await this.request(route('cartAdd', '/cart/add.js'), formData, {
          formData: true,
          open: shouldOpen,
          announce: text('added', 'Produto adicionado à sacola.')
        });
        if (!shouldOpen) notify(text('added', 'Produto adicionado à sacola.'));
        return true;
      } catch (error) {
        notify(error.message || text('cartUpdateError', 'Não foi possível adicionar o produto.'));
        return false;
      } finally {
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        }
      }
    }

    async addVariant(variantId, button) {
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');
      return this.add(formData, button, { open: true });
    }

    async changeLine(key, quantity) {
      if (!key) return;
      const normalized = Math.max(0, Number(quantity) || 0);
      try {
        await this.request(route('cartChange', '/cart/change.js'), { id: String(key), quantity: normalized }, {
          lineKey: key,
          open: true,
          announce: normalized === 0 ? text('cartItemRemoved', 'Item removido.') : text('cartQuantityUpdated', 'Quantidade atualizada.')
        });
      } catch (error) {
        notify(error.message || text('cartUpdateError', 'Não foi possível atualizar a sacola.'));
        await this.refresh({ open: true });
      }
    }

    scheduleQuantity(key, quantity) {
      window.clearTimeout(this.quantityTimers.get(key));
      const timer = window.setTimeout(() => {
        this.quantityTimers.delete(key);
        this.changeLine(key, quantity);
      }, 280);
      this.quantityTimers.set(key, timer);
    }

    async saveNote(note) {
      try {
        const response = await fetch(route('cartUpdate', '/cart/update.js'), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ note })
        });
        if (!response.ok) throw new Error();
        this.updateStatus(text('cartNoteSaved', 'Observação salva.'));
      } catch {
        notify(text('cartUpdateError', 'Não foi possível salvar a observação.'));
      }
    }

    async applyDiscount(code, button) {
      const value = String(code || '').trim();
      if (!value) {
        notify(text('discountRequired', 'Digite um cupom.'));
        return;
      }
      button?.setAttribute('aria-busy', 'true');
      if (button) button.disabled = true;
      this.updateStatus(text('discountApplying', 'Aplicando cupom…'));
      try {
        await this.request(route('cartUpdate', '/cart/update.js'), { discount: value }, {
          open: true,
          announce: text('discountProcessed', 'Cupom processado.')
        });
      } catch (error) {
        notify(error.message || text('cartUpdateError', 'Não foi possível aplicar o cupom.'));
      } finally {
        button?.removeAttribute('aria-busy');
        if (button) button.disabled = false;
      }
    }

    async open() {
      const mobileClose = $('[data-menu-close]');
      if (mobileClose && $('[data-mobile-menu][aria-hidden="false"]')) mobileClose.click();
      await this.refresh({ open: true });
      document.dispatchEvent(new CustomEvent('urb:cart:open'));
    }

    close() {
      closeDialog(this.dialog);
      document.dispatchEvent(new CustomEvent('urb:cart:close'));
    }

    onClick(event) {
      const open = event.target.closest('[data-cart-open]');
      if (open) {
        event.preventDefault();
        this.open();
        return;
      }
      const close = event.target.closest('[data-cart-close]');
      if (close) {
        this.close();
        return;
      }
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        // The full cart page owns its own remove link and uses data-cart-key.
        if (!this.dialog?.contains(remove)) return;
        event.preventDefault();
        window.clearTimeout(this.quantityTimers.get(remove.dataset.key));
        this.quantityTimers.delete(remove.dataset.key);
        this.changeLine(remove.dataset.key, 0);
        return;
      }
      const step = event.target.closest('[data-cart-step]');
      if (step) {
        event.preventDefault();
        const key = step.dataset.key;
        const input = $(`[data-cart-quantity][data-key="${selectorEscape(key)}"]`, this.dialog || document);
        if (!input) return;
        const increment = Math.max(1, Number(step.dataset.increment || input.step || 1));
        const minimum = Math.max(1, Number(input.dataset.min || 1));
        const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const current = Math.max(0, Number(input.value || 0));
        let next;
        if (step.dataset.cartStep === 'up') next = current === 0 ? minimum : current + increment;
        else {
          const candidate = current - increment;
          next = candidate < minimum ? 0 : candidate;
        }
        next = Math.min(maximum, Math.max(0, next));
        input.value = String(next);
        this.scheduleQuantity(key, next);
        return;
      }
      const crossAdd = event.target.closest('[data-cart-cross-add]');
      if (crossAdd) {
        event.preventDefault();
        this.addVariant(crossAdd.dataset.variantId, crossAdd);
        return;
      }
      const discount = event.target.closest('[data-cart-apply-discount]');
      if (discount) {
        event.preventDefault();
        const input = $('[data-cart-discount]', this.dialog || document);
        this.applyDiscount(input?.value, discount);
        return;
      }
      const blocked = event.target.closest('[data-cart-checkout][aria-disabled="true"]');
      if (blocked) {
        event.preventDefault();
        notify(text('acceptTerms', 'Aceite os termos para continuar.'));
        $('[data-cart-terms]', this.dialog || document)?.focus();
      }
    }

    onChange(event) {
      const quantity = event.target.closest('[data-cart-quantity]');
      if (quantity && quantity.closest('#CartDrawer')) {
        const minimum = Math.max(1, Number(quantity.dataset.min || 1));
        const increment = Math.max(1, Number(quantity.step || 1));
        const maximum = quantity.max ? Number(quantity.max) : Number.POSITIVE_INFINITY;
        let value = Math.max(0, Number(quantity.value || 0));
        if (value > 0) value = minimum + Math.max(0, Math.round((value - minimum) / increment)) * increment;
        value = Math.min(maximum, value);
        quantity.value = String(value);
        this.scheduleQuantity(quantity.dataset.key, value);
        return;
      }
      const terms = event.target.closest('[data-cart-terms]');
      if (terms && terms.closest('#CartDrawer')) {
        const checkout = $('[data-cart-checkout]', this.dialog || document);
        checkout?.setAttribute('aria-disabled', String(!terms.checked));
        try { sessionStorage.setItem(termsKey, terms.checked ? '1' : '0'); } catch { /* ignored */ }
      }
    }

    onInput(event) {
      const note = event.target.closest('[data-cart-note]');
      if (!note || !note.closest('#CartDrawer')) return;
      window.clearTimeout(this.noteTimer);
      this.noteTimer = window.setTimeout(() => this.saveNote(note.value), 650);
    }

    onSubmit(event) {
      const checkout = event.target.closest('[data-cart-checkout-form]');
      if (!checkout) return;
      const terms = $('[data-cart-terms]', this.dialog || document);
      if (terms && !terms.checked) {
        event.preventDefault();
        notify(text('acceptTerms', 'Aceite os termos para continuar.'));
        terms.focus();
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * Global predictive search.
   * ------------------------------------------------------------------ */
  class SearchDrawerController {
    constructor() {
      this.controller = null;
      this.timer = null;
      this.cache = new Map();
      this.boundClick = this.onClick.bind(this);
      this.boundInput = this.onInput.bind(this);
      this.boundSubmit = this.onSubmit.bind(this);
      this.boundKeydown = this.onKeydown.bind(this);
      document.addEventListener('click', this.boundClick);
      document.addEventListener('input', this.boundInput);
      document.addEventListener('submit', this.boundSubmit);
      document.addEventListener('keydown', this.boundKeydown);
      setupDialog(this.dialog);
      this.renderIdle();
    }

    get dialog() { return $('#SearchDrawer'); }
    get input() { return $('[data-predictive-input]', this.dialog || document); }
    get results() { return $('[data-predictive-results]', this.dialog || document); }
    get status() { return $('[data-predictive-status]', this.dialog || document); }
    get clearButton() { return $('[data-search-clear]', this.dialog || document); }

    settings() {
      const data = this.dialog?.dataset || {};
      return {
        enabled: bool(data.predictiveEnabled),
        types: data.resourceTypes || 'product',
        limit: Math.max(1, Number(data.resultsLimit || 6)),
        min: Math.max(1, Number(data.minCharacters || 2)),
        debounce: Math.max(100, Number(data.debounce || 250)),
        unavailable: data.unavailableProducts || 'last',
        showImages: bool(data.showImages),
        showPrices: bool(data.showPrices),
        showCompare: bool(data.showComparePrice),
        showPix: bool(data.showPixPrice),
        pixDiscount: Math.max(0, Math.min(100, Number(data.pixDiscount || 0))),
        showVendor: bool(data.showVendor),
        showType: bool(data.showProductType),
        showAvailability: bool(data.showAvailability),
        recent: bool(data.enableRecent),
        recentCount: Math.max(1, Number(data.recentCount || 5)),
        clearRecent: bool(data.showClearRecent),
        viewAll: bool(data.showViewAll),
        closeOnSelect: bool(data.closeOnSelect),
        autofocus: bool(data.autofocus)
      };
    }

    recentSearches() {
      if (!this.settings().recent) return [];
      try {
        let raw = localStorage.getItem(recentKey);
        if (!raw) {
          raw = localStorage.getItem(recentLegacyKey);
          if (raw) localStorage.setItem(recentKey, raw);
        }
        const value = JSON.parse(raw || '[]');
        return Array.isArray(value) ? value.filter((item) => typeof item === 'string').slice(0, this.settings().recentCount) : [];
      } catch {
        return [];
      }
    }

    remember(term) {
      const value = String(term || '').trim();
      if (!this.settings().recent || value.length < this.settings().min) return;
      const normalized = value.toLocaleLowerCase(cfg.locale || 'pt-BR');
      const next = [value, ...this.recentSearches().filter((item) => item.toLocaleLowerCase(cfg.locale || 'pt-BR') !== normalized)]
        .slice(0, this.settings().recentCount);
      try { localStorage.setItem(recentKey, JSON.stringify(next)); } catch { /* ignored */ }
    }

    clearRecent() {
      try { localStorage.removeItem(recentKey); } catch { /* ignored */ }
      this.renderIdle();
      if (this.status) this.status.textContent = text('recentCleared', 'Buscas recentes removidas.');
    }

    setStatus(value) {
      if (this.status) this.status.textContent = value || '';
    }

    renderIdle() {
      const results = this.results;
      const template = $('[data-search-idle-template]', this.dialog || document);
      if (!results || !template) return;
      results.replaceChildren(template.content.cloneNode(true));
      const recent = this.recentSearches();
      const section = $('[data-recent-searches]', results);
      const mount = $('[data-recent-search-chips]', results);
      if (section && mount && recent.length) {
        section.hidden = false;
        recent.forEach((term) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.searchTerm = term;
          button.textContent = term;
          mount.append(button);
        });
      }
      this.setStatus('');
      this.input?.setAttribute('aria-expanded', 'false');
    }

    resultGroup(title, count) {
      const section = document.createElement('section');
      section.className = 'search-drawer__result-group';
      const head = document.createElement('div');
      head.className = 'search-drawer__result-group-head';
      const heading = document.createElement('h3');
      heading.textContent = title;
      const total = document.createElement('span');
      total.textContent = String(count);
      head.append(heading, total);
      section.append(head);
      return section;
    }

    productCard(product, settings) {
      const card = document.createElement('a');
      card.className = 'search-result-product';
      card.href = safeUrl(product.url);
      card.dataset.searchResult = '';
      card.dataset.searchTermMemory = this.input?.value.trim() || '';
      if (!settings.showImages) card.classList.add('search-result-product--no-image');

      if (settings.showImages) {
        const media = document.createElement('span');
        media.className = 'search-result-product__media';
        const source = imageUrl(product.image, 520);
        if (source) {
          const image = document.createElement('img');
          image.src = source;
          image.alt = product.image_alt || product.title || '';
          image.width = 390;
          image.height = 520;
          image.loading = 'lazy';
          image.decoding = 'async';
          media.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'search-result-product__placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          media.append(placeholder);
        }
        if (settings.showAvailability && product.available === false) {
          const flag = document.createElement('em');
          flag.className = 'search-result-product__flag';
          flag.textContent = text('soldOut', 'Esgotado');
          media.append(flag);
        }
        card.append(media);
      }

      if (settings.showVendor && product.vendor) {
        const vendor = document.createElement('span');
        vendor.className = 'search-result-product__vendor';
        vendor.textContent = product.vendor;
        card.append(vendor);
      }
      const productType = product.type || product.product_type;
      if (settings.showType && productType) {
        const type = document.createElement('span');
        type.className = 'search-result-product__type';
        type.textContent = productType;
        card.append(type);
      }
      const title = document.createElement('strong');
      title.className = 'search-result-product__title';
      title.textContent = product.title || '';
      card.append(title);

      if (settings.showPrices && product.price !== undefined) {
        const commerce = document.createElement('span');
        commerce.className = 'search-result-product__commerce';
        const price = document.createElement('span');
        price.className = 'search-result-product__price';
        const current = document.createElement('strong');
        current.textContent = formatMoney(product.price);
        price.append(current);
        const compareValue = product.compare_at_price_max ?? product.compare_at_price;
        if (settings.showCompare && toCents(compareValue) > toCents(product.price)) {
          const compare = document.createElement('s');
          compare.textContent = formatMoney(compareValue);
          price.append(compare);
        }
        if (settings.showPix && settings.pixDiscount > 0) {
          const pix = document.createElement('span');
          pix.className = 'search-result-product__pix';
          const pixValue = Math.floor(toCents(product.price) * (100 - settings.pixDiscount) / 100);
          const pixPrice = document.createElement('b');
          pixPrice.textContent = formatMoney(pixValue);
          const pixLabel = document.createElement('small');
          pixLabel.textContent = 'via PIX';
          const pixDiscount = document.createElement('em');
          pixDiscount.textContent = `${settings.pixDiscount}% OFF`;
          pix.append(pixPrice, pixLabel, pixDiscount);
          commerce.append(pix);
        }
        commerce.append(price);
        card.append(commerce);
      }
      return card;
    }

    linkGroup(title, items, typeLabel) {
      const group = this.resultGroup(title, items.length);
      const list = document.createElement('div');
      list.className = 'search-drawer__link-list';
      items.forEach((item) => {
        const link = document.createElement('a');
        link.href = safeUrl(item.url);
        link.dataset.searchResult = '';
        link.dataset.searchTermMemory = this.input?.value.trim() || '';
        const name = document.createElement('span');
        name.textContent = item.title || item.text || '';
        const type = document.createElement('small');
        type.textContent = typeLabel;
        link.append(name, type);
        list.append(link);
      });
      group.append(list);
      return group;
    }

    queryGroup(items) {
      const group = this.resultGroup(text('searchSuggestionsLabel', 'Sugestões'), items.length);
      const list = document.createElement('div');
      list.className = 'search-drawer__query-list';
      items.forEach((item) => {
        const term = item.text || item.title || String(item.styled_text || '').replace(/<[^>]+>/g, '');
        if (!term) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.searchTerm = term;
        button.dataset.searchResult = '';
        button.textContent = term;
        list.append(button);
      });
      group.append(list);
      return group;
    }

    renderResults(resources, term) {
      const results = this.results;
      if (!results) return;
      const settings = this.settings();
      const products = resources.products || [];
      const collections = resources.collections || [];
      const pages = resources.pages || [];
      const articles = resources.articles || [];
      const queries = resources.queries || [];
      const count = products.length + collections.length + pages.length + articles.length + queries.length;
      results.replaceChildren();

      if (!count) {
        const empty = document.createElement('div');
        empty.className = 'search-drawer__empty';
        const message = document.createElement('p');
        message.textContent = text('noResultsFor', 'Nenhum resultado para “__TERM__”.', { term });
        empty.append(message);
        results.append(empty);
        this.setStatus(text('resultsCount', '__COUNT__ resultados', { count: 0 }));
        this.input?.setAttribute('aria-expanded', 'false');
        return;
      }

      const layout = document.createElement('div');
      layout.className = 'search-drawer__result-layout';
      const main = document.createElement('div');
      main.className = 'search-drawer__result-main';
      const aside = document.createElement('aside');
      aside.className = 'search-drawer__result-aside';

      if (products.length) {
        const group = this.resultGroup(text('searchProductsLabel', 'Produtos'), products.length);
        const grid = document.createElement('div');
        grid.className = 'search-drawer__product-grid';
        products.forEach((product) => grid.append(this.productCard(product, settings)));
        group.append(grid);
        main.append(group);
      }
      if (queries.length) aside.append(this.queryGroup(queries));
      if (collections.length) aside.append(this.linkGroup(text('searchCollectionsLabel', 'Coleções'), collections, text('searchCollectionType', 'Coleção')));
      if (pages.length) aside.append(this.linkGroup(text('searchPagesLabel', 'Páginas'), pages, text('searchPageType', 'Página')));
      if (articles.length) aside.append(this.linkGroup(text('searchArticlesLabel', 'Artigos'), articles, text('searchArticleType', 'Artigo')));
      if (main.childElementCount && aside.childElementCount) layout.append(main, aside);
      else {
        layout.classList.add('is-single');
        layout.append(main.childElementCount ? main : aside);
      }
      results.append(layout);

      if (settings.viewAll) {
        const all = document.createElement('a');
        all.className = 'search-drawer__view-all';
        all.href = `${cfg.routes?.search || '/search'}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        all.dataset.searchResult = '';
        all.dataset.searchTermMemory = term;
        all.textContent = text('viewAllResults', 'Ver todos os resultados');
        results.append(all);
      }

      this.setStatus(text('resultsCount', '__COUNT__ resultados', { count }));
      this.input?.setAttribute('aria-expanded', 'true');
    }

    renderError(term) {
      const results = this.results;
      if (!results) return;
      results.replaceChildren();
      const empty = document.createElement('div');
      empty.className = 'search-drawer__empty';
      const message = document.createElement('p');
      message.textContent = text('searchError', 'Não foi possível concluir a busca.');
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'search-drawer__error-action';
      retry.dataset.searchRetry = term;
      retry.textContent = text('searchRetry', 'Tentar novamente');
      empty.append(message, retry);
      results.append(empty);
      this.setStatus(message.textContent);
    }

    predictiveUrl(term) {
      const settings = this.settings();
      let path = cfg.routes?.predictive || '/search/suggest';
      if (!path.endsWith('.json')) path = `${path}.json`;
      const url = new URL(path, window.location.origin);
      url.searchParams.set('q', window.URBSearch?.predictiveTerm(term) || term);
      url.searchParams.set('resources[type]', settings.types);
      url.searchParams.set('resources[limit]', String(settings.limit));
      url.searchParams.set('resources[options][unavailable_products]', settings.unavailable);
      url.searchParams.set('resources[options][fields]', window.URBSearch?.predictiveFields(term) || 'title,product_type,variants.title,variants.sku,vendor,tag');
      return url.href;
    }

    cancelSearch() {
      window.clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
      this.dialog?.classList.remove('is-loading');
    }

    async search(query, { force = false } = {}) {
      this.cancelSearch();
      const term = String(query || '').trim();
      const settings = this.settings();
      if (this.clearButton) this.clearButton.hidden = !term;
      if (!settings.enabled) {
        this.renderIdle();
        return;
      }
      if (term.length < settings.min) {
        this.controller?.abort();
        this.renderIdle();
        if (term) this.setStatus(text('minimumCharacters', 'Digite pelo menos __COUNT__ caracteres.', { count: settings.min }));
        return;
      }
      const cacheKey = `${settings.types}:${settings.unavailable}:${term.toLocaleLowerCase(cfg.locale || 'pt-BR')}`;
      if (!force && this.cache.has(cacheKey)) {
        this.renderResults(this.cache.get(cacheKey), term);
        return;
      }
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.dialog?.classList.add('is-loading');
      this.setStatus(text('searching', 'Buscando…'));
      try {
        const response = await fetch(this.predictiveUrl(term), {
          signal: controller.signal,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error('search');
        const payload = await response.json();
        if (controller.signal.aborted || this.controller !== controller) return;
        const rawResources = payload.resources?.results || {};
        const resources = window.URBSearch?.filterPredictive(rawResources, term) || rawResources;
        this.cache.set(cacheKey, resources);
        if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
        this.renderResults(resources, term);
      } catch (error) {
        if (error.name !== 'AbortError' && this.controller === controller) this.renderError(term);
      } finally {
        if (this.controller === controller) this.dialog?.classList.remove('is-loading');
      }
    }

    schedule(query) {
      this.cancelSearch();
      this.timer = window.setTimeout(() => this.search(query), this.settings().debounce);
    }

    open() {
      const mobileClose = $('[data-menu-close]');
      if (mobileClose && $('[data-mobile-menu][aria-hidden="false"]')) mobileClose.click();
      setupDialog(this.dialog);
      openDialog(this.dialog, this.settings().autofocus ? '[data-predictive-input]' : '[data-search-close]');
      if (!this.input?.value) this.renderIdle();
      document.dispatchEvent(new CustomEvent('urb:search:open'));
    }

    close() {
      this.cancelSearch();
      closeDialog(this.dialog);
      document.dispatchEvent(new CustomEvent('urb:search:close'));
    }

    fill(term) {
      if (!this.input) return;
      this.input.value = term;
      if (this.clearButton) this.clearButton.hidden = false;
      this.input.focus();
      this.search(term);
    }

    clear() {
      this.cancelSearch();
      if (!this.input) return;
      this.input.value = '';
      if (this.clearButton) this.clearButton.hidden = true;
      this.renderIdle();
      this.input.focus();
    }

    onClick(event) {
      const open = event.target.closest('[data-search-open]');
      if (open) {
        event.preventDefault();
        this.open();
        return;
      }
      if (!this.dialog?.contains(event.target)) return;
      if (event.target.closest('[data-search-close]')) {
        this.close();
        return;
      }
      if (event.target.closest('[data-search-clear]')) {
        this.clear();
        return;
      }
      if (event.target.closest('[data-search-clear-recent]')) {
        this.clearRecent();
        return;
      }
      const term = event.target.closest('[data-search-term]');
      if (term) {
        this.fill(term.dataset.searchTerm || term.textContent);
        return;
      }
      const retry = event.target.closest('[data-search-retry]');
      if (retry) {
        this.search(retry.dataset.searchRetry, { force: true });
        return;
      }
      const result = event.target.closest('[data-search-result]');
      if (result) {
        const remembered = result.dataset.searchTermMemory || this.input?.value;
        this.remember(remembered);
        if (this.settings().closeOnSelect && result.tagName === 'A') this.close();
      }
    }

    onInput(event) {
      if (!event.target.matches('[data-predictive-input]')) return;
      if (this.clearButton) this.clearButton.hidden = !event.target.value;
      this.schedule(event.target.value);
    }

    onSubmit(event) {
      const form = event.target.closest('[data-global-search-form]');
      if (!form) return;
      const value = this.input?.value.trim();
      if (!value) {
        event.preventDefault();
        this.input?.focus();
        return;
      }
      this.remember(value);
    }

    onKeydown(event) {
      if (!this.dialog?.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const nodes = $$('[data-search-result], [data-search-term]', this.dialog).filter((node) => node.offsetParent !== null);
      if (!nodes.length) return;
      event.preventDefault();
      const index = nodes.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      nodes[(next + nodes.length) % nodes.length].focus();
    }
  }

  const cart = new CartDrawerController();
  const search = new SearchDrawerController();

  window.URBDrawers = {
    cart: {
      add: (formData, button, options) => cart.add(formData, button, options),
      addVariant: (variantId, button) => cart.addVariant(variantId, button),
      open: () => cart.open(),
      close: () => cart.close(),
      refresh: (options) => cart.refresh(options)
    },
    search: {
      open: () => search.open(),
      close: () => search.close(),
      search: (query, options) => search.search(query, options)
    }
  };

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.matches?.('.cart-drawer-section') || event.target.querySelector?.('[data-cart-drawer]')) {
      cart.prepareCurrentDialog();
      cart.syncCountFromMarkup();
    }
  });
})();
